import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API as API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatDateTime, formatNum, formatKg } from "../lib/nutrition";
import { NutriScoreBadge } from "../components/NutriScoreBadge";
import { Plus, MagnifyingGlass, PencilSimple, Trash, FilePdf, FileXls, Copy, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

async function downloadFile(url, filename) {
  const token = localStorage.getItem("nc_token");
  const res = await fetch(url, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Export fehlgeschlagen");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export default function Recipes() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/recipes");
      setItems(data);
    } catch (err) {
      console.error("Fehler beim Laden der Rezepte:", err);
      toast.error("Rezepte konnten nicht geladen werden");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? items.filter((r) => r.name.toLowerCase().includes(s)) : items;
  }, [items, q]);

  const handleDelete = async () => {
    if (!delId) return;
    try {
      await api.delete(`/recipes/${delId}`);
      toast.success("Rezept gelöscht");
      setDelId(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const exportRecipe = async (r, kind) => {
    try {
      const url = `${API_BASE}/recipes/${r.id}/export/${kind}`;
      const ext = kind === "pdf" ? "pdf" : kind === "label" ? "pdf" : "xlsx";
      const filename = kind === "label"
        ? `nutricalc_etiketten_${r.name.replace(/\s+/g, "_")}.pdf`
        : `nutricalc_${r.name.replace(/\s+/g, "_")}.${ext}`;
      await downloadFile(url, filename);
      toast.success(kind === "label" ? "Etiketten-PDF heruntergeladen" : `${kind.toUpperCase()} heruntergeladen`);
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };

  const duplicateRecipe = async (r) => {
    try {
      const { data } = await api.post(`/recipes/${r.id}/duplicate`);
      toast.success(`"${data.name}" erstellt`);
      load();
    } catch {
      toast.error("Duplizieren fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <header className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Rezepte</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Meine Rezepte</h1>
          <p className="text-sm text-zinc-500 mt-1">Zutaten kombinieren und Nährwerte automatisch berechnen.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none min-w-0">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Suchen…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-full sm:w-64 rounded-sm"
              data-testid="recipe-search-input"
            />
          </div>
          <Link to="/rezepte/neu" className="shrink-0">
            <Button className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm" data-testid="new-recipe-button">
              <Plus size={14} className="mr-2" /> Neues Rezept
            </Button>
          </Link>
        </div>
      </header>

      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
              <th className="px-5 py-3 font-medium">Rezept</th>
              <th className="px-5 py-3 font-medium text-right">Zutaten</th>
              <th className="px-5 py-3 font-medium text-right">Gewicht</th>
              <th className="px-5 py-3 font-medium text-right">kcal gesamt</th>
              <th className="px-5 py-3 font-medium text-right">kcal/100g</th>
              <th className="px-5 py-3 font-medium">Nutri-Score</th>
              <th className="px-5 py-3 font-medium">Erstellt von</th>
              <th className="px-5 py-3 font-medium">Datum</th>
              <th className="px-5 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-zinc-500" data-testid="empty-recipes-list">
                  Noch keine Rezepte. <Link to="/rezepte/neu" className="text-[#002FA7] underline">Erstes Rezept anlegen</Link>.
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`recipe-row-${r.id}`}>
                <td className="px-5 py-3">
                  <Link to={`/rezepte/${r.id}`} className="font-medium hover:text-[#002FA7]">
                    {r.name}
                  </Link>
                  {r.description && <div className="text-xs text-zinc-500 truncate max-w-xs">{r.description}</div>}
                </td>
                <td className="px-5 py-3 text-right font-mono-num">{r.items.length}</td>
                <td className="px-5 py-3 text-right font-mono-num">{formatKg(r.total_grams)} kg</td>
                <td className="px-5 py-3 text-right font-mono-num">{formatNum(r.totals?.energy_kcal || 0)}</td>
                <td className="px-5 py-3 text-right font-mono-num">{formatNum(r.per_100g?.energy_kcal || 0)}</td>
                <td className="px-5 py-3"><NutriScoreBadge score={r.nutri_score} size="sm" /></td>
                <td className="px-5 py-3 text-zinc-600">{r.created_by_name}</td>
                <td className="px-5 py-3 text-zinc-500 font-mono-num text-xs">{formatDateTime(r.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => exportRecipe(r, "label")}
                      data-testid={`export-label-${r.id}`}
                      title="Etiketten-PDF (8×4 cm)">
                      <Tag size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => exportRecipe(r, "pdf")}
                      data-testid={`export-pdf-${r.id}`}>
                      <FilePdf size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => exportRecipe(r, "xlsx")}
                      data-testid={`export-xlsx-${r.id}`}>
                      <FileXls size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => duplicateRecipe(r)}
                      data-testid={`duplicate-recipe-${r.id}`}>
                      <Copy size={14} />
                    </Button>
                    <Link to={`/rezepte/${r.id}`}>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`edit-recipe-${r.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                    </Link>
                    <Button size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDelId(r.id)}
                      data-testid={`delete-recipe-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent data-testid="delete-recipe-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rezept löschen?</AlertDialogTitle>
            <AlertDialogDescription>Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-recipe">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
